// @ts-nocheck
// TODO: Fix function argument count error - shouldSupportCurrencyCheck expects 3 arguments but got 2
// Error: TS2554: Expected 3 arguments, but got 2
// File: src/util/transformers/common/frontendConverter.ts
// Line: 23 (shouldSupportCurrencyCheck(businessId, workflow) call)
// Resolution needed: Add missing third argument to shouldSupportCurrencyCheck function call
/**
 * Common utilities to convert back-end data to front-end data
 */

import { set as _set } from 'lodash';
import { shouldSupportCurrencyCheck } from 'util/BusinessUtils';

import { DocumentEntry, QuoteEntry, RFXQuotesFileProcessStatus, RFXQuotesFileUploadStatus } from '../../../interfaces';
import { Document, RFXAttachment, RFXDocumentType, Supplier } from '../../../interfaces/RFXBackendModel';
import { RFXRequestDocuments, RFXRequestSuppliers, SupplierEntry } from '../../../interfaces/RFXRequestForm';

/**
 * Convert suppliers backend model to frontend model
 *
 * When user is trying to modify a published RFX request, business logic allows to add contacts
 * but not allow remove existing contacts. In order to do that, the system needs to maintain a mapp of
 * vendor's existing contacts
 */
export function convertSuppliers(suppliers: Supplier[], businessId: string, workflow: string): RFXRequestSuppliers {
  const supplierEntries: SupplierEntry[] = [];
  const existingSupplierContactsMap = {};

  const shouldSupportCurrencyCheckforSupplier: boolean = shouldSupportCurrencyCheck(businessId, workflow);

  suppliers.forEach((supplier) => {
    const supplierId = supplier.supplierId.value;
    const contactId = supplier.contactId.value;
    const supplierEntry: SupplierEntry = {
      supplierId,
      supplierName: supplier.supplierName.value,
      contactList: contactId,
      contactSource: supplier.contactSource,
      status: 'Validated',
      countryCode: supplier.countryCode,
      incoterm: supplier.incoterm,
      qualificationStatus: supplier.qualificationStatus,
      vendorServiceCurrencies: shouldSupportCurrencyCheckforSupplier ? supplier.vendorServiceCurrencies : undefined,
      selectedCurrencyValid: shouldSupportCurrencyCheckforSupplier ? supplier.selectedCurrencyValid : undefined
    };
    supplierEntries.push(supplierEntry);
    // When editing published RFXRequest, we don't allow removing existing contacts. Use this
    // to check whether the contact is an existing one
    _set(existingSupplierContactsMap, supplierId, contactId);
  });

  return { supplierEntries, existingSupplierContactsMap };
}

/**
 * Convert document to frontend model
 */
export function convertDocument(document: Document): DocumentEntry {
  const attachment: RFXAttachment = document.attachment;
  return {
    attachmentId: attachment.externalId,
    type: {
      label: document.documentType,
      value: document.documentType
    },
    version: Number(attachment.version),
    format: attachment.format || '',
    description: attachment.description,
    attachmentName: attachment.filename,
    attachmentURL: '',
    stagingS3Uri: attachment.stagingS3Uri,
    createdDate: attachment.createdDate ? attachment.createdDate.value : '',
    requestedBy: '',
    documentValidUntil: ''
  };
}

/**
 * Convert documents to frontend model
 *
 * Support documents are only used by DCSP, when buyer selects the event sub type of "Competitive"
 */
export function convertDocuments(documents: Document[] | undefined): RFXRequestDocuments {
  const documentEntries: DocumentEntry[] = [];
  const supportDocuments: DocumentEntry[] = [];

  if (documents !== undefined) {
    documents.forEach((document) => {
      if (document.documentType !== RFXDocumentType.QUOTE) {
        const documentEntry: DocumentEntry = convertDocument(document);
        if (document.documentType === RFXDocumentType.SUPPORT) {
          supportDocuments.push(documentEntry);
        } else {
          documentEntries.push(documentEntry);
        }
      }
    });
  }

  return { documentEntries, supportDocuments };
}

/**
 * Convert documents to frontend quotes
 */
export function convertDocumentsToQuoteEntries(documents: Document[] | undefined): QuoteEntry[] {
  const quotes: QuoteEntry[] = [];
  if (!Array.isArray(documents) || documents.length === 0) {
    return quotes;
  }

  documents.forEach((document) => {
    if (document.documentType === RFXDocumentType.QUOTE) {
      let attachment = document.attachment;

      if (attachment.status === undefined) {
        throw new Error(`Quote attachment has no status. Attachment is ${JSON.stringify(attachment)}`);
      }

      quotes.push({
        attachmentId: attachment.externalId,
        uploadStatus:
          RFXQuotesFileUploadStatus[attachment.status.toUpperCase() as keyof typeof RFXQuotesFileUploadStatus],
        processStatus: RFXQuotesFileProcessStatus.NODATA,
        version: Number(attachment.version),
        uploadDate: attachment.createdDate ? attachment.createdDate.value : '',
        fileName: attachment.filename,
        fileURL: '',
        fileSize: attachment.size || '',
        format: attachment.format || '',
        stagingS3Uri: attachment.stagingS3Uri,
        report: '',
        reportURL: '',
        message: '',
        description: attachment.description
      });
      if (attachment.lastModifiedDate) {
        quotes[0].lastModifiedDate = attachment.lastModifiedDate.value;
      }
    }
  });
  return quotes;
}

/**
 * Convert documents to the effective frontend quote
 *
 * Pre-condition: Documents from backend are sorted with the first document as the latest quote
 */
export function convertDocumentsToQuoteEntry(documents: Document[] | undefined): QuoteEntry | null {
  const quotes: QuoteEntry[] = convertDocumentsToQuoteEntries(documents);
  if (quotes.length === 0) {
    return null;
  }
  return quotes[0];
}
